import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { CircleIcon } from 'lucide-react';
import { motion } from 'motion/react';

import { cn } from '@components/utils/classname';

// Context for passing variant and size from RadioGroup to RadioGroupItem
const RadioGroupContext = React.createContext<{
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
}>({});

const radioGroupItemVariants = cva(
  // Base styles - focus ring shows on keyboard focus only (focus-visible)
  'aspect-square shrink-0 rounded-full border shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: [
          // Default state - matches Checkbox primary
          'border-border bg-background text-primary',
          // Checked state
          'data-[state=checked]:border-primary',
          // Focus state - ring color follows primary variant
          'focus-visible:border-primary focus-visible:ring-primary/30',
          // Invalid state
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        ],
        secondary: [
          // Default state - matches Checkbox secondary
          'border-border bg-background text-secondary-foreground',
          // Checked state
          'data-[state=checked]:border-secondary-foreground',
          // Focus state - ring color follows secondary variant
          'focus-visible:border-secondary focus-visible:ring-secondary/30',
          // Invalid state
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
        ],
      },
      size: {
        sm: 'size-3.5',
        md: 'size-4',
        lg: 'size-5',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

const radioGroupIndicatorVariants = cva(
  'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fill-current',
  {
    variants: {
      variant: {
        primary: 'fill-primary',
        secondary: 'fill-secondary-foreground',
      },
      size: {
        sm: 'size-2', // 8px inner circle for 14px radio
        md: 'size-3', // 12px inner circle for 16px radio
        lg: 'size-4', // 16px inner circle for 20px radio
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

interface RadioGroupProps
  extends React.ComponentProps<typeof RadioGroupPrimitive.Root>,
    VariantProps<typeof radioGroupItemVariants> {}

function RadioGroup({ className, variant, size, ...props }: RadioGroupProps) {
  return (
    <RadioGroupContext.Provider
      value={{
        variant: variant ?? undefined,
        size: size ?? undefined,
      }}
    >
      <RadioGroupPrimitive.Root
        data-slot="radio-group"
        className={cn('grid gap-3', className)}
        {...props}
      />
    </RadioGroupContext.Provider>
  );
}

interface RadioGroupItemProps
  extends React.ComponentProps<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioGroupItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, variant: variantProp, size: sizeProp, ...props }, ref) => {
  const context = React.useContext(RadioGroupContext);
  const variant = variantProp ?? context.variant ?? 'primary';
  const size = sizeProp ?? context.size ?? 'md';

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      data-slot="radio-group-item"
      className={cn(radioGroupItemVariants({ variant, size }), className)}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        data-slot="radio-group-indicator"
        className="relative flex items-center justify-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30,
          }}
        >
          <CircleIcon
            className={cn(radioGroupIndicatorVariants({ variant, size }))}
          />
        </motion.div>
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export { RadioGroup, RadioGroupItem };
